import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ArrowLeft, Loader2 } from 'lucide-react'
import {
  getCatalog, getWallet, listInvoices,
  type Catalog, type Invoice, type LedgerEntry,
} from '@/api/billing'
import { useRazorpayCheckout } from '@/hooks/useRazorpayCheckout'
import { refreshCredits } from '@/store/creditsStore'
import BalanceCard from './BalanceCard'
import PlanCard from './PlanCard'
import TopUpPacks from './TopUpPacks'
import TransactionsTable from './TransactionsTable'
import InvoicesList from './InvoicesList'
import UsageDashboard from './UsageDashboard'

/**
 * The user's billing home: balance, plan, top-ups, credit history, invoices,
 * and — on its own tab — what the account's AI has actually consumed.
 *
 * Usage lives here rather than in Settings because it is denominated in the
 * same credits as the balance above it: seeing what is left and what was spent
 * should not mean crossing the app.
 *
 * `?topup=1` deep-links here from the out-of-credits modal and scrolls straight
 * to the pack grid, so the remedy is one click from the refusal. `?tab=usage`
 * opens the usage tab (the old Settings → Usage link forwards to it).
 */
export default function BillingPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { checkout, busy } = useRazorpayCheckout()

  const [catalog, setCatalog] = useState<Catalog | null>(null)
  const [entries, setEntries] = useState<LedgerEntry[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<'billing' | 'usage'>(
    params.get('tab') === 'usage' ? 'usage' : 'billing',
  )
  const packsRef = useRef<HTMLDivElement | null>(null)

  const load = useCallback(async () => {
    try {
      const [cat, wallet, inv] = await Promise.all([
        getCatalog(),
        getWallet({ limit: 50 }),
        listInvoices().catch(() => ({ invoices: [] })),
      ])
      setCatalog(cat)
      setEntries(wallet.entries)
      setInvoices(inv.invoices)
    } catch {
      toast.error('Could not load your billing information')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // Deep link from the "out of credits" modal — land the user on the packs.
  useEffect(() => {
    if (!loading && tab === 'billing' && params.get('topup') === '1' && packsRef.current) {
      packsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [loading, params, tab])

  const buyPack = useCallback(async (packCode: string, couponCode?: string) => {
    const result = await checkout({
      purpose: 'credit_pack',
      target_ref: packCode,
      coupon_code: couponCode,
      description: 'Twasta.ai credits',
    })
    if (result.cancelled) return
    if (!result.ok) {
      toast.error(result.error || 'The payment did not complete')
      return
    }
    toast.success('Credits added')
    refreshCredits()
    await load()
  }, [checkout, load])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <Loader2 className="spin" size={28} />
      </div>
    )
  }

  const currentPlan = catalog?.plans.find((p) => p.is_current) || null

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '1.5rem 1rem 3rem' }}>
      <button
        className="btn btn-secondary"
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/builder'))}
        style={{ marginBottom: '1rem', gap: '0.375rem' }}
      >
        <ArrowLeft size={16} /> Back
      </button>

      <h1 style={{ fontSize: '1.5625rem', margin: '0 0 1rem' }}>Billing &amp; credits</h1>

      <div style={{
        display: 'flex', gap: '0.25rem',
        borderBottom: '1px solid var(--color-border)', marginBottom: '1.25rem',
      }}>
        {([
          { key: 'billing', label: 'Billing', icon: 'credit_card' },
          { key: 'usage', label: 'Usage', icon: 'bar_chart' },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 20px', background: 'transparent', border: 'none',
              fontSize: '0.9375rem',
              fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              borderBottom: tab === t.key ? '2px solid var(--color-accent)' : '2px solid transparent',
              cursor: 'pointer', marginBottom: -1,
            }}
          >
            <span className="material-icons" style={{ fontSize: 17 }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'usage' ? <UsageDashboard /> : (
      <>
      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <BalanceCard
          summary={catalog?.balance ?? null}
          subscription={catalog?.subscription ?? null}
          onTopUp={() => packsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
        />
        <PlanCard
          plan={currentPlan}
          subscription={catalog?.subscription ?? null}
          onChanged={load}
        />
      </div>

      <div ref={packsRef} style={{ marginTop: '1rem' }}>
        <TopUpPacks packs={catalog?.packs ?? []} busy={busy} onBuy={buyPack} />
      </div>

      <div style={{ marginTop: '1rem' }}>
        <TransactionsTable entries={entries} />
      </div>

      <div style={{ marginTop: '1rem' }}>
        <InvoicesList invoices={invoices} />
      </div>
      </>
      )}
    </div>
  )
}
